import { atom } from 'jotai';
import { atomWithLazy, loadable } from 'jotai/utils';
import { IFormulaFieldDataModel } from '../../../data-models/formula-field.data-model';
import { readThroughAtom } from '../../boot-cache/bootCache';
import { getDisplayedFormulaFieldIds } from '../../queries/FieldDisplayConfigQueries';
import { fetchFormulaFields } from '../../queries/MaggieFormulaFieldQueries';

/**
 * showFormulaFields kill switch, synced from LaunchDarkly by
 * FormulaFieldsFlagSync (LD flags live in React context; atoms and
 * MetricsCalculator read this instead). Defaults to off: nothing
 * formula-related fetches, computes, or renders until the flag arrives true.
 *
 * `undefined` is "LD has not answered yet", distinct from a resolved `false`.
 * FD-5049: without that distinction the gated atoms below resolve to `[]` with
 * state 'hasData', so "there are no formula fields" and "we have not looked
 * yet" look identical to consumers — see formulaColumnsReadyAtom. Both gates
 * still read it as a plain boolean, so `undefined` keeps the fetches off.
 */
export const formulaFieldsEnabledAtom = atom<boolean | undefined>(undefined);

/** Raw list — fetches on first read. Write target for refresh after CRUD. */
export const formulaFieldsListAtom = atomWithLazy<
  IFormulaFieldDataModel[] | Promise<IFormulaFieldDataModel[]>
>((): Promise<IFormulaFieldDataModel[]> =>
  readThroughAtom(formulaFieldsListAtom, 'formulaFields', fetchFormulaFields)
);

/**
 * What consumers read. Disabled → resolves to [] WITHOUT touching
 * formulaFieldsListAtom, so the /field/formula request never fires and every
 * downstream surface (grid columns, row evaluation, Field Manager rows)
 * uniformly sees "no formula fields".
 */
const gatedFormulaFieldsListAtom = atom(async (get) => {
  if (!get(formulaFieldsEnabledAtom)) {
    return [] as IFormulaFieldDataModel[];
  }
  return get(formulaFieldsListAtom);
});

export const formulaFieldsListLoadable = loadable(gatedFormulaFieldsListAtom);

export const formulaFieldsByKeyAtom = atom(async (get) => {
  const fields = (await get(gatedFormulaFieldsListAtom)) ?? [];
  return new Map<string, IFormulaFieldDataModel>(fields.map((field) => [field.key, field]));
});

/**
 * Ids registered in fieldDisplayConfig — the fields whose column renders.
 * Separate from the field list because display config is a registration table
 * (shared with KPIs), not a property of the field. Write target for refresh
 * after a display change.
 */
export const displayedFormulaFieldIdsAtom = atomWithLazy<number[] | Promise<number[]>>(
  (): Promise<number[]> =>
    readThroughAtom(displayedFormulaFieldIdsAtom, 'formulaDisplayIds', getDisplayedFormulaFieldIds)
);

/**
 * Gated like the list: with the flag off nothing is displayed and the
 * /fieldDisplayConfig/formula request never fires.
 */
const gatedDisplayedFormulaFieldIdsAtom = atom(async (get) => {
  if (!get(formulaFieldsEnabledAtom)) {
    return [] as number[];
  }
  return get(displayedFormulaFieldIdsAtom);
});

export const displayedFormulaFieldIdsLoadable = loadable(
  atom(async (get) => new Set(await get(gatedDisplayedFormulaFieldIdsAtom)))
);

/**
 * Whether the formula-field column set is final, so a column pipeline can wait
 * for it rather than publishing a column list that is missing it (FD-5049).
 *
 * The loadable states alone can't answer this: while the flag is unresolved the
 * gated atoms short-circuit to `[]` as 'hasData' without fetching, which reads
 * as "loaded, and there are none". So resolve the flag first.
 *
 * 'hasError' counts as settled — a failed /field/formula must release whatever
 * is waiting rather than block it forever.
 */
export const formulaColumnsReadyAtom = atom((get) => {
  const enabled = get(formulaFieldsEnabledAtom);
  if (enabled === undefined) {
    return false;
  }
  if (!enabled) {
    return true;
  }
  return (
    get(formulaFieldsListLoadable).state !== 'loading' &&
    get(displayedFormulaFieldIdsLoadable).state !== 'loading'
  );
});
